import React from 'react';
import { get, post } from '../../custom_libraries/serverRequests';
import globalVariables from '../../custom_libraries/globalVariables';
import urls from '../../custom_libraries/urls';
import Alert from "@mui/material/Alert";
import CheckIcon from "@mui/icons-material/Check";
import Backdrop from '@mui/material/Backdrop';
import WebLogo from '../../assets/Artboard 1Bonus.png';
import './TrueThreatSmsSettings.css';
import { Button, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

/* ---------- Validation helpers ---------- */

// Accepts a domain (chat.example.com), localhost, or an IPv4 address.
const HOSTNAME_REGEX = /^(localhost|([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}|\d{1,3}(\.\d{1,3}){3})$/i;

// Rocket.Chat has no fixed URL format: it is self-hosted (or cloud hosted at
// <workspace>.rocket.chat), so the server can live on any domain, port or sub-path.
// We therefore validate for a well-formed https URL rather than a specific pattern.
export const validateRocketChatUrl = (value) => {
  const url = (value || '').trim();

  if (!url) return 'Rocket Chat URL is required.';
  if (/\s/.test(url)) return 'URL must not contain spaces.';
  if (!/^https?:\/\/.+/i.test(url)) { //accepts both http and https for local testing, but https is recommended
  return 'URL must start with http:// or https:// (e.g. https://chat.yourcompany.com).';
}
  // if (!/^https:\/\//i.test(url)) return 'URL must start with https:// (e.g. https://chat.yourcompany.com).';

  let parsed;
  try {
    parsed = new URL(url);
  } catch (e) {
    return 'Enter a valid URL, e.g. https://chat.yourcompany.com';
  }

  if (parsed.username || parsed.password) return 'URL must not contain a username or password.';
  if (!HOSTNAME_REGEX.test(parsed.hostname)) return 'Enter a valid domain name, e.g. chat.yourcompany.com';

  return '';
};

const FIELD_LABELS = {
  rocket_chat_channel_url: 'Rocket Chat URL',
  acc_username: 'Username',
  acc_password: 'Password',
};

/* ---------- Styles ---------- */

const styles = {
  row: {
    display: 'grid',
    width: '100%',
    boxSizing: 'border-box',
    // minmax(0, 1fr) lets the input column shrink/stay fixed instead of growing
    // with the width of its content (e.g. a long error message)
    gridTemplateColumns: 'minmax(110px, 150px) minmax(0, 1fr)',
    columnGap: '12px',
    alignItems: 'start',
    marginBottom: '4px',
  },
  label: {
    paddingTop: '9px', // lines the label text up with the input's text
    fontWeight: 500,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    fontSize: '14px',
    borderRadius: '4px',
    border: '1px solid #b5b5b5',
    outline: 'none',
  },
  inputError: {
    border: '1px solid #d32f2f',
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '12px',
    lineHeight: '16px',
    marginTop: '3px',
    minHeight: '16px', // reserves one line; longer messages wrap onto further lines
    // width:0 + minWidth:100% means the text takes the width of the input column
    // but never adds to it, so the message wraps below the input instead of widening it.
    width: 0,
    minWidth: '100%',
    boxSizing: 'border-box',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
};

class RocketChatSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      enabled: false,
      acc_username: '',
      acc_password: '',
      rocket_chat_channel_url: '',
      response_message: '',
      field_errors: {},      // per-field validation messages
      error2: false,
      success: false,
      open_progress_bar: false,
    };
    this.alertTimer = null;
  }

  componentWillUnmount() {
    clearTimeout(this.alertTimer);
  }

  componentDidMount() {
    this.fetchUserSettings();
  }

  //   handleToggle = () => {
  //     this.setState((prevState) => ({ enabled: !prevState.enabled }));
  //   };

  handleChange = (event) => {
    const { name, value } = event.target;
    // Clear the error of the field being edited as soon as the user types
    this.setState((prev) => ({
      [name]: value,
      field_errors: { ...prev.field_errors, [name]: '' },
    }));
  };

  // Validate a single field when the user leaves it
  handleBlur = (event) => {
    const { name } = event.target;
    // Only validate on blur for the URL (avoids showing "required" while tabbing through)
    if (name === 'rocket_chat_channel_url' && this.state[name].trim()) {
      const message = validateRocketChatUrl(this.state[name]);
      this.setState((prev) => ({ field_errors: { ...prev.field_errors, [name]: message } }));
    }
  };

  startLoading = () => {
    this.setState({ open_progress_bar: true });
  };

  stopLoading = () => {
    this.setState({ open_progress_bar: false });
  };

  togglePasswordVisibility = () => {
    this.setState((prev) => ({ show_password: !prev.show_password }));
  };

  showAlert = (newState) => {
    clearTimeout(this.alertTimer);

    this.setState({
      success: false,
      error2: false,
      response_message: '',
      ...newState,
    });

    this.alertTimer = setTimeout(() => {
      this.setState({
        success: false,
        error2: false,
        response_message: '',
      });
    }, 5000);
  };

  /**
   * Validates all fields.
   * Returns { isValid, fieldErrors, message } where `message` is the summary
   * shown in the alert at the top of the form.
   */
  validateForm = () => {
    const { rocket_chat_channel_url, acc_username, acc_password } = this.state;
    const values = {
      rocket_chat_channel_url: rocket_chat_channel_url.trim(),
      acc_username: acc_username.trim(),
      acc_password: acc_password, // do not trim passwords
    };

    const fieldErrors = {};
    const emptyFields = Object.keys(values).filter((key) => !values[key]);

    emptyFields.forEach((key) => {
      fieldErrors[key] = `${FIELD_LABELS[key]} is required.`;
    });

    // Only check the URL format if something was entered
    if (values.rocket_chat_channel_url) {
      const urlError = validateRocketChatUrl(values.rocket_chat_channel_url);
      if (urlError) fieldErrors.rocket_chat_channel_url = urlError;
    }

    let message = '';
    if (emptyFields.length === 3) {
      message = 'All fields are required. Please enter the Rocket Chat URL, username and password.';
    } else if (emptyFields.length > 0) {
      message = `Please fill in the required field${emptyFields.length > 1 ? 's' : ''}: ${emptyFields
        .map((key) => FIELD_LABELS[key])
        .join(', ')}.`;
    } else if (fieldErrors.rocket_chat_channel_url) {
      message = fieldErrors.rocket_chat_channel_url;
    }

    return { isValid: Object.keys(fieldErrors).length === 0, fieldErrors, message };
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    // ---- Validate before calling the API ----
    const { isValid, fieldErrors, message } = this.validateForm();
    this.setState({ field_errors: fieldErrors });
    if (!isValid) {
      this.showAlert({ error2: true, response_message: message });
      return;
    }

    const { rocket_chat_channel_url, acc_username, acc_password } = this.state;

    try {
      // Convert to API format
      let post_data = {
        rocket_chat_url: rocket_chat_channel_url.trim(),
        username: acc_username.trim(),
        password: acc_password,
      };

      post_data = JSON.stringify(post_data);

      this.startLoading();

      let access_token = localStorage.getItem(globalVariables.AUTH_TOKEN);
      let headers = {
        'content-type': 'application/json',
        'Authorization': `Bearer ${access_token}`,
      };

      let response = await post(urls.ROCKET_CHAT_LOGIN, post_data, headers);
      if (response?.password_configured === true) {
        this.showAlert({ success: true, response_message: response?.message });
        this.fetchUserSettings(); // Refresh the form with the saved values
      } else {
        this.showAlert({
          error2: true,
          response_message: response?.message || 'An error occurred while saving the settings.',
        });
      }
    } catch (error) {
      console.error('Error saving Rocket Chat settings:', error);
      this.showAlert({
        error2: true,
        response_message: 'Something went wrong while connecting to Rocket Chat. Please try again.',
      });
    } finally {
      this.stopLoading();
    }
  };

    fetchUserSettings = async() => {
        try {
            //   this.startLoading();
            let access_token = localStorage.getItem(globalVariables.AUTH_TOKEN)

            let headers = {
            'content-type': 'application/json',
            'Authorization': `Bearer ${access_token}`
            }


            const response = await get(urls.ROCKET_CHAT_LOGIN, headers);

            if (response.status === 200) {
            this.setState({
                acc_username: response.username || '',
                acc_password: response.password || '',
                rocket_chat_channel_url: response.rocket_chat_url || '',
            });
            }
            else {
                this.showAlert({error2:true, response_message: response?.message || 'Either no information found or an error occurred while getting the information.',});
            }

        } catch (error) {
            console.error("Error fetching user settings:", error);
            this.showAlert({error2:true, response_message: 'An error occurred while getting the information.',});
        }
    };

renderField = ({ name, label, type, placeholder, autoComplete }) => {
    const { field_errors, show_password } = this.state;
    const error = field_errors[name];
    const inputId = `rocket-chat-${name}`;
    const isPassword = type === 'password';

    return (
      <div style={styles.row}>
        <label htmlFor={inputId} style={styles.label}>
          {label}
        </label>
        <div style={{ minWidth: 0 }}>
          {/* Wrapper lets the eye button sit inside the input's right edge */}
          <div style={{ position: 'relative' }}>
            <input
              id={inputId}
              type={isPassword && show_password ? 'text' : type}
              className={`sms-input${isPassword ? ' rocket-chat-password-input' : ''}`}
              name={name}
              value={this.state[name]}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              placeholder={placeholder}
              autoComplete={autoComplete}
              aria-label={label}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${inputId}-error` : undefined}
              style={{
                ...styles.input,
                ...(isPassword ? { paddingRight: '40px' } : {}),
                ...(error ? styles.inputError : {}),
              }}
            />
            {isPassword && (
              <IconButton
                type="button"
                size="small"
                onClick={this.togglePasswordVisibility}
                aria-label={show_password ? 'Hide password' : 'Show password'}
                title={show_password ? 'Hide password' : 'Show password'}
                edge="end"
                style={{ position: 'absolute', top: '50%', right: '6px', transform: 'translateY(-50%)' }}
              >
                {show_password ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            )}
          </div>
          <div id={`${inputId}-error`} role={error ? 'alert' : undefined} style={styles.errorText}>
            {error}
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { error2, success, response_message } = this.state;

    return (
      <div className="true-threat-sms-page">
        <div
          className="side-by-side-container true-threat-sms-layout"
          style={{ flex: '2', marginLeft: '5%', padding: '20px', overflowY: 'auto', gap: '20px', width: '90%' }}
        >
          <div className="health-setting-container true-threat-sms-settings" style={{ width: '50%' }}>
            <div>
              {error2 ? (
                <Alert severity="error">{response_message}</Alert>
              ) : success ? (
                <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
                  {response_message}
                </Alert>
              ) : null}
            </div>

            <h3 style={{ textAlign: 'center' }}>Rocket Chat Account Settings</h3>

            {/* noValidate: we show our own messages instead of the browser's pop-ups */}
            <form className="true-threat-sms-form" onSubmit={this.handleSubmit} noValidate style={{ marginTop: '25px', width: '100%', minWidth: 0 }}>
              {this.renderField({
                name: 'rocket_chat_channel_url',
                label: 'Rocket Chat URL',
                type: 'url',
                placeholder: 'https://chat.yourcompany.com',
                autoComplete: 'url',
              })}
              {this.renderField({
                name: 'acc_username',
                label: 'Username',
                type: 'text',
                placeholder: 'Enter your username',
                autoComplete: 'username',
              })}
              {this.renderField({
                name: 'acc_password',
                label: 'Password',
                type: 'password',
                placeholder: 'Enter your password',
                autoComplete: 'current-password',
              })}

              {/* Button sits under the inputs column */}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 150px) minmax(0, 1fr)', columnGap: '12px', marginTop: '8px' }}>
                <span />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button type="submit" id="rowsPerPageButton">
                    Save
                  </Button>
                </div>
              </div>
            </form>
          </div>

          <div className="health-setting-container true-threat-sms-settings" style={{ width: '50%' }}>
            <div className="form-section" style={{ fontFamily: 'Arial, sans-serif', width: '100%' }}>
              <h4>How it works?</h4>
              <p style={{ fontSize: '15px', marginBottom: '7px', textAlign: 'justify' }}>
                <span style={{ color: 'lightseagreen' }}>Rocket Chat Account Credentials</span> <br />
                <br />
                <strong>-</strong> Please specify your Rocket Chat URL, username and password to connect your account.
                <br />
                <strong>-</strong> The URL must start with https:// (for example, https://chat.yourcompany.com).
                <br />
                <strong>-</strong> Once connected, you can send messages to your Rocket Chat channels or users directly
                from the application.
                <br />
                <strong>-</strong> Ensure that your Rocket Chat account has the necessary permissions to send messages.
              </p>
            </div>
          </div>
        </div>

        {/* Backdrop with Circular Progress */}
        <Backdrop open={this.state.open_progress_bar}>
          <div className="custom-spinner">
            <div className="circle"></div>
            <div className="circle tilted"></div>
            <img
              src={this.state.storedLogo ? this.state.storedLogo : WebLogo}
              alt="web-logo"
              style={{ backgroundColor: '#001f54', borderRadius: '100px' }}
            />
          </div>
        </Backdrop>
      </div>
    );
  }
}

export default RocketChatSettings;